import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Search, Plus, Trash2, Save, X, Loader2, RefreshCw,
  AlertCircle, Pencil, Filter,
} from "lucide-react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Human labels for the underlying field keys, so the "Field" column reads as
// "Pant Front Pocket" instead of "pant_front_pocket". Falls back to the raw key.
const FIELD_LABELS = {
  lapel_type: "Lapel Type",
  lapel_width: "Lapel Width",
  collar_construction: "Collar Construction",
  jacket_buttonhole: "Buttonhole",
  front_closure: "Front Closure",
  vent_style: "Vent Style",
  back_vent_length: "Back Vent Length",
  shoulder_posture: "Shoulder Posture",
  canvas_construction: "Canvas",
  sleeve_pad: "Sleeve Pad",
  waist_pocket_type: "Waist Pocket",
  waist_pocket_angle: "Waist Pocket Angle",
  ticket_pocket: "Ticket Pocket",
  chest_pocket: "Chest Pocket",
  exterior_stitch: "Exterior Stitch",
  lower_pocket_combo: "Lower Pocket Combo",
  lining_style: "Lining Style",
  monogram_jacket: "Jacket Monogram",
  // Pant
  pant_body_style: "Pant Body Style",
  pant_front_style: "Pant Front Style",
  pant_pleat_depth: "Pleat Depth",
  pant_front_pocket: "Pant Front Pocket",
  pant_front_pocket_bartack: "Front Pocket Bartack",
  pant_front_pocket_stitch: "Front Pocket Stitch",
  pant_watch_pocket: "Watch Pocket",
  pant_inner_watch_pocket: "Inner Watch Pocket",
  pant_back_pocket_style: "Back Pocket Style",
  pant_back_pocket_closure: "Back Pocket Closure",
  pant_bottom_finishing: "Bottom Finishing",
  pant_front_closure: "Pant Front Closure",
  pant_waistband_style: "Waistband Style",
  pant_waistband_width: "Waistband Width",
  // Vest
  vest_lapel: "Vest Lapel",
  vest_front_closure: "Vest Front Closure",
  vest_pocket_type: "Vest Pocket Type",
  vest_pocket_number: "Vest Pocket Count",
  vest_back: "Vest Back",
  // Shirt
  shirt_collar_style: "Shirt Collar Style",
  shirt_collar_height: "Collar Height",
  shirt_collar_stays: "Collar Stays",
  shirt_cuff_style: "Cuff Style",
  shirt_placket: "Placket",
  shirt_pocket: "Shirt Pocket",
  shirt_back: "Shirt Back",
  shirt_bottom: "Shirt Bottom",
  shirt_monogram: "Shirt Monogram",
  shirt_button_style: "Shirt Buttons",
};

const fieldLabel = (f) => FIELD_LABELS[f] || f.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

// Garment bucket → color tint
const bucketOf = (f) =>
  f.startsWith("shirt_") ? "shirt" :
  f.startsWith("pant_")  ? "pant"  :
  f.startsWith("vest_")  ? "vest"  :
  "jacket";

const BUCKET_COLOR = {
  jacket: "amber",
  pant:   "blue",
  vest:   "purple",
  shirt:  "rose",
};

export default function AdminCodeManagerPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [fieldFilter, setFieldFilter] = useState("");
  const [editing, setEditing] = useState(null);  // {code,field,value,display_label, _prev:{field,value}}
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [resetConfirm, setResetConfirm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/rc-codes`);
      setRows(res.data?.rows || []);
      setFields(res.data?.fields || []);
    } catch (e) {
      toast.error("Failed to load codes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let r = rows;
    if (fieldFilter) r = r.filter((x) => x.field === fieldFilter);
    const needle = q.trim().toLowerCase();
    if (needle) {
      r = r.filter((x) =>
        x.code.toLowerCase().includes(needle) ||
        x.value.toLowerCase().includes(needle) ||
        x.field.toLowerCase().includes(needle) ||
        (x.display_label || "").toLowerCase().includes(needle),
      );
    }
    return r;
  }, [rows, q, fieldFilter]);

  const counts = useMemo(() => {
    const byBucket = { jacket: 0, pant: 0, vest: 0, shirt: 0 };
    rows.forEach((r) => byBucket[bucketOf(r.field)]++);
    return byBucket;
  }, [rows]);

  const save = async () => {
    if (!editing) return;
    try {
      setBusy(true);
      const isNew = creating;
      const body = {
        code: editing.code.trim(),
        field: editing.field.trim(),
        value: editing.value.trim(),
        display_label: (editing.display_label || "").trim() || null,
      };
      if (isNew) {
        await axios.post(`${API}/rc-codes`, body);
        toast.success(`Added ${body.code}`);
      } else {
        await axios.put(`${API}/rc-codes`, body, {
          params: {
            prev_field: editing._prev?.field,
            prev_value: editing._prev?.value,
          },
        });
        toast.success(`Updated ${body.code}`);
      }
      setEditing(null);
      setCreating(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete mapping ${row.field} → "${row.value}" (${row.code})?`)) return;
    try {
      await axios.delete(`${API}/rc-codes`, { params: { field: row.field, value: row.value } });
      toast.success(`Deleted ${row.code}`);
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const resetAll = async () => {
    try {
      setBusy(true);
      const res = await axios.post(`${API}/rc-codes/reset`);
      toast.success(`Reset complete — ${res.data?.count} rows`);
      setResetConfirm(false);
      load();
    } catch {
      toast.error("Reset failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a1628] text-foreground" data-testid="code-manager-page">
      <header className="sticky top-0 z-30 bg-[#0a1628]/95 backdrop-blur border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/admin")}
            className="text-slate-400 hover:text-white"
            data-testid="back-btn"
          >
            <ArrowLeft size={16} className="mr-1" /> Back
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold flex items-center gap-2">
              <Pencil size={16} className="text-amber-400" /> RC Code Manager
            </h1>
            <p className="text-[10px] text-slate-500">
              Master code → field/value map. Edits flow into POS & Quick Order on next page load.
            </p>
          </div>
          <div className="flex items-center gap-3 text-[10px] text-slate-500">
            <span className="text-amber-400">Jacket: <b>{counts.jacket}</b></span>
            <span className="text-blue-400">Pant: <b>{counts.pant}</b></span>
            <span className="text-purple-400">Vest: <b>{counts.vest}</b></span>
            <span className="text-rose-400">Shirt: <b>{counts.shirt}</b></span>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={load}
            className="text-slate-400 hover:text-white"
            title="Reload"
          >
            <RefreshCw size={14} />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setResetConfirm(true)}
            className="text-red-400 hover:text-red-300 text-xs"
            data-testid="reset-btn"
          >
            <AlertCircle size={14} className="mr-1" /> Reset
          </Button>
          <Button
            size="sm"
            onClick={() => {
              setEditing({ code: "", field: fields[0] || "lapel_type", value: "", display_label: "" });
              setCreating(true);
            }}
            className="bg-amber-500 hover:bg-amber-600 text-black"
            data-testid="add-btn"
          >
            <Plus size={14} className="mr-1" /> Add code
          </Button>
        </div>
        <div className="max-w-7xl mx-auto px-6 pb-3 flex items-center gap-2">
          <div className="relative flex-1 max-w-md">
            <Search size={12} className="absolute left-2.5 top-2.5 text-slate-500" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search code, label, field…"
              className="pl-8 h-8 text-xs bg-card border-white/10"
              data-testid="search-input"
            />
          </div>
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <Filter size={12} />
            <Select value={fieldFilter || "__all__"} onValueChange={(v) => setFieldFilter(v === "__all__" ? "" : v)}>
              <SelectTrigger className="h-8 w-[220px] text-xs bg-card border-white/10" data-testid="field-filter">
                <SelectValue placeholder="All fields" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="__all__">All fields ({rows.length})</SelectItem>
                {fields.map((f) => (
                  <SelectItem key={f} value={f}>
                    {fieldLabel(f)} <span className="text-slate-500 text-[10px] ml-1">{f}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <span className="ml-auto text-[10px] text-slate-500">{filtered.length} shown</span>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-500">
            <Loader2 size={20} className="animate-spin mr-2" /> Loading codes…
          </div>
        ) : (
          <div className="bg-card border border-white/5 rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-white/5 text-slate-400">
                <tr>
                  <th className="text-left px-3 py-2 font-medium w-28">Code</th>
                  <th className="text-left px-3 py-2 font-medium">Field (dropdown)</th>
                  <th className="text-left px-3 py-2 font-medium">Value (label)</th>
                  <th className="text-left px-3 py-2 font-medium">Custom Label</th>
                  <th className="px-3 py-2 w-24"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center text-slate-500 py-8">
                      No codes match the current filter.
                    </td>
                  </tr>
                ) : (
                  filtered.map((row) => {
                    const bucket = bucketOf(row.field);
                    const color = BUCKET_COLOR[bucket];
                    return (
                      <tr
                        key={`${row.field}|${row.value}`}
                        className="border-t border-white/5 hover:bg-white/[0.02] transition-colors"
                        data-testid={`row-${row.code}-${row.field}`}
                      >
                        <td className="px-3 py-1.5">
                          <code className={`text-[11px] font-mono text-${color}-400 bg-${color}-500/10 px-1.5 py-0.5 rounded`}>
                            {row.code}
                          </code>
                        </td>
                        <td className="px-3 py-1.5">
                          <span className="text-slate-300">{fieldLabel(row.field)}</span>
                          <span className="text-slate-600 ml-1.5 text-[9px]">{row.field}</span>
                        </td>
                        <td className="px-3 py-1.5 text-slate-200">{row.value}</td>
                        <td className="px-3 py-1.5 text-amber-300">
                          {row.display_label || <span className="text-slate-600">—</span>}
                        </td>
                        <td className="px-3 py-1.5 flex items-center gap-1 justify-end">
                          <button
                            onClick={() => setEditing({ ...row, _prev: { field: row.field, value: row.value } })}
                            className="text-slate-400 hover:text-amber-400 p-1"
                            data-testid={`edit-${row.code}-${row.field}`}
                            title="Edit"
                          >
                            <Pencil size={12} />
                          </button>
                          <button
                            onClick={() => remove(row)}
                            className="text-slate-400 hover:text-red-400 p-1"
                            data-testid={`delete-${row.code}-${row.field}`}
                            title="Delete"
                          >
                            <Trash2 size={12} />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* Edit / Create dialog */}
      <Dialog
        open={!!editing}
        onOpenChange={(o) => { if (!o) { setEditing(null); setCreating(false); } }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-sm">
              {creating ? "Add factory code" : `Edit ${editing?.code}`}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3 py-2">
              <div>
                <label className="text-[10px] text-slate-400 block mb-1">Factory code</label>
                <Input
                  value={editing.code}
                  onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase() })}
                  placeholder="C-0001"
                  className="text-xs h-8"
                  data-testid="edit-code"
                />
              </div>
              <div>
                <label className="text-[10px] text-slate-400 block mb-1">Field (dropdown)</label>
                <Select value={editing.field} onValueChange={(v) => setEditing({ ...editing, field: v })}>
                  <SelectTrigger className="h-8 text-xs" data-testid="edit-field">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {fields.map((f) => (
                      <SelectItem key={f} value={f}>
                        {fieldLabel(f)} <span className="text-slate-500 text-[10px] ml-1">{f}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] text-slate-400 block mb-1">Value (option label shown in dropdown)</label>
                <Input
                  value={editing.value}
                  onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                  placeholder="Notch"
                  className="text-xs h-8"
                  data-testid="edit-value"
                />
              </div>
              <div>
                <label className="text-[10px] text-slate-400 block mb-1">
                  Custom display label <span className="text-slate-600">(optional rename for the badge)</span>
                </label>
                <Input
                  value={editing.display_label || ""}
                  onChange={(e) => setEditing({ ...editing, display_label: e.target.value })}
                  placeholder="Leave blank to use the value above"
                  className="text-xs h-8"
                  data-testid="edit-display-label"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setEditing(null); setCreating(false); }}
              disabled={busy}
            >
              <X size={12} className="mr-1" /> Cancel
            </Button>
            <Button
              size="sm"
              onClick={save}
              disabled={busy || !editing?.code || !editing?.field || !editing?.value}
              className="bg-amber-500 hover:bg-amber-600 text-black"
              data-testid="save-btn"
            >
              {busy ? <Loader2 size={12} className="animate-spin mr-1" /> : <Save size={12} className="mr-1" />}
              {creating ? "Add" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset confirm */}
      <Dialog open={resetConfirm} onOpenChange={setResetConfirm}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-sm text-red-400 flex items-center gap-2">
              <AlertCircle size={14} /> Reset to defaults?
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-400 py-2">
            This wipes every edit you've made and re-seeds the master mapping from the bundled <code className="text-amber-400">rc_codes.json</code> (471 rows). Cannot be undone.
          </p>
          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setResetConfirm(false)} disabled={busy}>
              Cancel
            </Button>
            <Button size="sm" onClick={resetAll} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-reset-btn">
              {busy ? <Loader2 size={12} className="animate-spin mr-1" /> : null}
              Reset everything
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
